import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  BadgeCheck,
  Sparkles,
  Network,
  Briefcase,
  Bot,
  Plug,
  Activity,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useExecution } from "@/store/executionStore";
import { agentRunsFromExecutions } from "@/lib/agents";
import { APPS } from "@/data/apps";
import type { ActivityKind } from "@/types";

const KIND: Record<ActivityKind, { icon: any; color: string; label: string }> = {
  verification: { icon: BadgeCheck, color: "#22c55e", label: "Verification" },
  specialization: { icon: Sparkles, color: "#a855f7", label: "Specialization" },
  routing: { icon: Network, color: "#10b981", label: "Routing" },
  job: { icon: Briefcase, color: "#3b82f6", label: "Job" },
  agent: { icon: Bot, color: "#f59e0b", label: "Agent" },
  integration: { icon: Plug, color: "#06b6d4", label: "Integration" },
};

export function ActivityCenter() {
  const { activityOpen, setActivity, activity } = useHive();
  const executions = useExecution((s) => s.executions);
  const running = agentRunsFromExecutions(executions).filter((a) => a.status === "running");

  return (
    <AnimatePresence>
      {activityOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[80]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActivity(false)}
          />
          <motion.div
            initial={{ x: 380, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 380, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="fixed right-3 top-[60px] z-[90] flex max-h-[calc(100vh-160px)] w-[360px] flex-col overflow-hidden rounded-2xl border border-app-strong glass-strong shadow-window"
          >
            <div className="flex items-center justify-between border-b border-app px-4 py-3">
              <div className="flex items-center gap-2">
                <Activity size={16} className="accent" />
                <span className="text-sm font-semibold text-app">AI Activity Center</span>
              </div>
              <button onClick={() => setActivity(false)} className="rounded-lg p-1 text-app-2 transition hover:bg-surface-2">
                <X size={16} />
              </button>
            </div>

            {/* Active agents */}
            <div className="border-b border-app p-3">
              <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-app-2">
                <span>Active agents</span>
                <span className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" /> {running.length} running
                </span>
              </div>
              <div className="space-y-2">
                {running.map((a) => {
                  const app = APPS[a.app];
                  return (
                    <div key={a.id} className="flex items-center gap-2.5">
                      <span className="grid h-7 w-7 place-items-center rounded-lg text-white" style={{ background: `linear-gradient(135deg,${app.accent},${app.accent}aa)` }}>
                        <app.icon size={13} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-app">{a.name}</span>
                          <span className="tabular-nums text-app-2">{Math.round(a.progress * 100)}%</span>
                        </div>
                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-surface-2">
                          <motion.div className="h-full rounded-full" style={{ background: app.accent }}
                            initial={{ width: 0 }} animate={{ width: `${a.progress * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Event stream */}
            <div className="flex-1 overflow-y-auto p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-app-2">
                Event stream
              </div>
              <div className="relative space-y-1 pl-4">
                <div className="absolute bottom-2 left-[6px] top-2 w-px bg-app" />
                <AnimatePresence initial={false}>
                  {activity.map((e) => {
                    const k = KIND[e.kind];
                    return (
                      <motion.div
                        key={e.id}
                        layout
                        initial={{ opacity: 0, x: 12, height: 0 }}
                        animate={{ opacity: 1, x: 0, height: "auto" }}
                        exit={{ opacity: 0 }}
                        className="relative py-1.5"
                      >
                        <span
                          className="absolute -left-[14px] top-2.5 grid h-3.5 w-3.5 place-items-center rounded-full border-2 border-surface"
                          style={{ background: k.color }}
                        />
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-app">{e.title}</div>
                            <div className="truncate text-[11px] text-app-2">{e.detail}</div>
                          </div>
                          <span className="shrink-0 text-[10px] text-app-2">{e.time}</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
